/**
 * 30-day trial tracking — web equivalent of iOS KeychainTrialService.
 * Uses localStorage (cleared if the user wipes site data; backend sync can replace later).
 */

const TRIAL_START_KEY = 'wt_trial_start_date';
const TRIAL_DURATION_DAYS = 30;

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBetween(from: Date, to: Date): number {
  const a = startOfLocalDay(from).getTime();
  const b = startOfLocalDay(to).getTime();
  return Math.floor((b - a) / 86_400_000);
}

export function readTrialStartDate(): Date | null {
  try {
    const raw = localStorage.getItem(TRIAL_START_KEY);
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/** Returns the trial start date, setting it to now if not already set. */
export function getOrSetTrialStartDate(): Date {
  const existing = readTrialStartDate();
  if (existing) return existing;
  const now = new Date();
  try {
    localStorage.setItem(TRIAL_START_KEY, now.toISOString());
  } catch {
    /* ignore quota */
  }
  return now;
}

export function isTrialActive(now: Date = new Date()): boolean {
  const trialStart = getOrSetTrialStartDate();
  return daysBetween(trialStart, now) < TRIAL_DURATION_DAYS;
}

export function trialDaysRemaining(now: Date = new Date()): number {
  const trialStart = getOrSetTrialStartDate();
  const elapsed = daysBetween(trialStart, now);
  return Math.max(0, TRIAL_DURATION_DAYS - elapsed);
}

/** Debug/testing only. */
export function resetTrialForTesting(): void {
  try {
    localStorage.removeItem(TRIAL_START_KEY);
  } catch {
    /* ignore */
  }
}
